import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CosmicBackground from '../components/CosmicBackground';
import LanguageSelector from '../components/LanguageSelector';
import MusicButton from '../components/MusicButton';
import ShareApp from '../components/ShareApp';
import Logo from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Sparkles, Zap, Crown, Moon, Heart, Star } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const features = [
    {
      icon: Sparkles,
      title: t('mysticalWisdom'),
      desc: t('mysticalWisdomDesc'),
    },
    {
      icon: Zap,
      title: t('dailyMessages'),
      desc: t('dailyMessagesDesc'),
    },
    {
      icon: Moon,
      title: language === 'ro' ? 'Interpretarea viselor' : 'Dream Interpretation',
      desc:
        language === 'ro'
          ? 'Descoperă mesajele ascunse din visele tale și primește claritate simbolică.'
          : 'Discover the hidden messages in your dreams and receive symbolic clarity.',
    },
    {
      icon: Star,
      title: language === 'ro' ? 'Tarot cosmic' : 'Cosmic Tarot',
      desc:
        language === 'ro'
          ? 'Primește ghidare prin cărți pentru prezent, alegeri și energie personală.'
          : 'Receive card guidance for your present, choices and personal energy.',
    },
    {
      icon: Heart,
      title: language === 'ro' ? 'Portalul iubirii' : 'Love Portal',
      desc:
        language === 'ro'
          ? 'Explorează conexiuni de suflet, suflet pereche și flacără geamănă.'
          : 'Explore soul connections, soulmate energy and twin flame signs.',
    },
    {
      icon: Crown,
      title: t('premiumAccess'),
      desc: t('premiumAccessDesc'),
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <CosmicBackground />

      <div
        className="fixed inset-0 bg-gradient-to-b from-[#1e3a8a] via-[#0f172a] to-black pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="relative z-10">
        <header
          className="sticky top-0 px-6 sm:px-12 z-50 bg-transparent border-b border-white/5 overflow-visible"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top) + 20px)',
            paddingBottom: '24px'
          }}
        >
          <div className="max-w-7xl mx-auto h-10 flex justify-between items-center">
            <div className="h-10 flex items-center shrink-0">
              <Logo size="sm" />
            </div>

            <div className="h-10 flex items-center justify-end gap-2 sm:gap-4 shrink-0 whitespace-nowrap">
              <div className="w-16 sm:w-20 h-10 flex items-center justify-center shrink-0">
                <LanguageSelector />
              </div>

              <div className="w-8 h-10 flex items-center justify-center shrink-0">
                <div className="scale-75 origin-center flex items-center justify-center">
                  <MusicButton />
                </div>
              </div>

              <div className="w-24 sm:w-32 h-10 flex items-center justify-end shrink-0">
                <motion.button
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/login')}
                  className="whitespace-nowrap text-slate-400 hover:text-white transition-colors text-xs sm:text-sm font-light tracking-wide sm:tracking-wider"
                  data-testid="login-nav-button"
                >
                  {t('signIn')}
                </motion.button>
              </div>
            </div>
          </div>
        </header>

        <section className="min-h-screen flex items-center justify-center px-6 pt-20 pb-32">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="text-center max-w-4xl"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
              className="flex justify-center mb-16 relative"
            >
              <div className="absolute inset-0 w-96 h-96 -left-48 -top-48 pointer-events-none">
                <div className="floating-star floating-star-1"></div>
                <div className="floating-star floating-star-2"></div>
                <div className="floating-star floating-star-3"></div>
                <div className="floating-star floating-star-4"></div>
                <div className="floating-star floating-star-5"></div>
                <div className="floating-star floating-star-6"></div>
              </div>

              <div className="energy-core">
                <div className="particle particle-1"></div>
                <div className="particle particle-2"></div>
                <div className="particle particle-3"></div>
                <div className="particle particle-4"></div>
                <div className="particle particle-5"></div>
                <div className="particle particle-6"></div>
                <div className="particle particle-7"></div>
                <div className="particle particle-8"></div>
                <div className="particle particle-9"></div>
                <div className="particle particle-10"></div>
                <div className="particle particle-11"></div>
                <div className="particle particle-12"></div>
                <div className="core-glow"></div>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="font-heading text-4xl sm:text-5xl md:text-8xl font-light tracking-[0.18em] text-white"
              style={{ fontWeight: 300, letterSpacing: '0.18em' }}
            >
              Universe
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mb-10"
            >
              <p className="text-xs opacity-70 mt-4 text-center text-slate-400">
                {language === 'ro'
                  ? 'Prin continuare, ești de acord cu '
                  : 'By continuing, you agree to '}
                <Link to="/terms" className="underline hover:text-white transition-colors">
                  {language === 'ro' ? 'Termeni și Condiții' : 'Terms & Conditions'}
                </Link>
                {language === 'ro' ? ', ' : ', '}
                <Link to="/privacy" className="underline hover:text-white transition-colors">
                  {language === 'ro' ? 'Politica de Confidențialitate' : 'Privacy Policy'}
                </Link>
                {language === 'ro' ? ' și ' : ' and '}
                <Link to="/disclaimer" className="underline hover:text-white transition-colors">
                  {language === 'ro' ? 'Declinarea responsabilității' : 'Disclaimer'}
                </Link>
                .
              </p>
            </motion.div>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/guest-ask')}
              className="cta-button text-white px-12 py-4 rounded-full text-lg font-light tracking-wider transition-all duration-500"
              data-testid="get-started-button"
            >
              {t('enterTheUniverse')}
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="mt-12 text-slate-500 text-sm font-light tracking-wide"
            >
              {t('cosmicWisdom')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 1 }}
              className="mt-8"
            >
              <ShareApp variant="ghost" />
            </motion.div>
          </motion.div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-40">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl font-light text-center text-white mb-24 tracking-wider"
          >
            {t('experienceCosmicConnection')}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-16">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="glass-card p-10 hover:border-white/20 transition-all duration-500 group text-center"
                data-testid={`feature-card-${idx}`}
              >
                <feature.icon className="w-12 h-12 text-cyan-400 mb-8 mx-auto group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-heading text-xl font-light text-white mb-5 tracking-wide">
                  {feature.title}
                </h3>
                <p className="text-slate-400 leading-relaxed font-light">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-40">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl font-light text-center text-white mb-24 tracking-wider"
          >
            {t('chooseYourPath')}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-12 text-center"
              data-testid="free-tier-card"
            >
              <h3 className="font-heading text-2xl font-light text-white mb-3 tracking-wide">
                {t('freePlan')}
              </h3>
              <div className="text-5xl font-light text-gradient-gold mb-10">$0</div>

              <ul className="space-y-5 mb-12 text-left">
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('oneQuestionPerDay')}</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('dailyCosmicMessages')}</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('questionHistoryFeature')}</span>
                </li>
              </ul>

              <button
                onClick={() => navigate('/guest-ask')}
                className="w-full py-4 rounded-full border border-white/20 text-white hover:bg-white/5 transition-all font-light tracking-wide"
                data-testid="free-signup-button"
              >
                {t('startFree')}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-12 border-violet-500/30 relative overflow-hidden text-center premium-card group"
              data-testid="premium-tier-card"
            >
              <div className="premium-particle premium-particle-1"></div>
              <div className="premium-particle premium-particle-2"></div>
              <div className="premium-particle premium-particle-3"></div>
              <div className="premium-particle premium-particle-4"></div>
              <div className="premium-particle premium-particle-5"></div>
              <div className="premium-particle premium-particle-6"></div>
              <div className="premium-particle premium-particle-7"></div>
              <div className="premium-particle premium-particle-8"></div>

              <div className="absolute top-4 right-4 bg-gradient-to-r from-cyan-500 to-violet-500 text-white px-4 py-1 rounded-full text-xs font-light tracking-wider">
                {t('popular')}
              </div>

              <h3 className="font-heading text-2xl font-light text-white mb-3 tracking-wide">
                {t('premiumPlan')}
              </h3>

              <div className="text-5xl font-light text-gradient-gold mb-2 relative z-10 price-target">
                $9.99
                <span className="text-base text-slate-400 font-light">/month</span>
              </div>

              <p className="text-xs text-gray-400 mt-4 text-center">
                {language === 'ro'
                  ? 'Prin continuare, ești de acord cu Termenii și Politica de confidențialitate.'
                  : 'By continuing, you agree to the Terms and Privacy Policy.'}
              </p>

              <ul className="space-y-5 mb-12 mt-8 text-left relative z-10">
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>
                    <strong className="text-white font-normal">{t('unlimited')}</strong>{' '}
                    {t('unlimitedQuestionsText')}
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('deeperUniversalInsights')}</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('enhancedIntuitiveGuidance')}</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('personalQuestionArchive')}</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300 font-light">
                  <span className="text-cyan-400 mt-1">✓</span>
                  <span>{t('priorityResponseProcessing')}</span>
                </li>
              </ul>

              <button
                onClick={() => navigate('/guest-ask')}
                className="cta-button w-full py-4 rounded-full text-white transition-all font-light tracking-wide relative z-10"
                data-testid="premium-signup-button"
              >
                {t('activatePremiumAccess')}
              </button>

              <p className="text-xs opacity-70 mt-4 text-center text-slate-400">
                {language === 'ro'
                  ? 'Prin continuare, ești de acord cu '
                  : 'By continuing, you agree to '}
                <Link to="/terms" className="underline hover:text-white transition-colors">
                  {language === 'ro' ? 'Termeni și Condiții' : 'Terms & Conditions'}
                </Link>
                {language === 'ro' ? ' și ' : ' and '}
                <Link to="/privacy" className="underline hover:text-white transition-colors">
                  {language === 'ro' ? 'Politica de Confidențialitate' : 'Privacy Policy'}
                </Link>
                .
              </p>
            </motion.div>
          </div>
        </section>

        <footer className="py-16 text-center text-slate-700 border-t border-white/5">
          <p className="text-sm font-light tracking-wide">{t('footerCopyright')}</p>
        </footer>
      </div>
    </div>
  );
};

export default Landing;
